// useProjectCollection — subscribe to a per-project Firestore subcollection.
// ─────────────────────────────────────────────────────────────────────────────
// App.js historically inlined ~18 identical onSnapshot listeners, one per
// per-project subcollection (variations, claims, diaries, auditLogs, …). This
// hook is the single, reusable version of that pattern: it owns the listener
// lifecycle (subscribe on projectId/name change, clean up on unmount) and
// returns the live docs array, exactly mirroring the old inline behaviour
//   onSnapshot(collection(db, "projects", pid, name),
//     snap => setX(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
//
// Use it for READ-ONLY collections (those whose state was only ever set by the
// listener + the project-change reset). Collections that also take optimistic
// local updates need the richer variant that also returns a setter — see the
// "Modularizing App.js" section in docs/ARCHITECTURE-MULTITENANCY.md.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// Pass { withSetter: true } for collections that also take OPTIMISTIC local
// updates in their handlers (e.g. setRfis(prev => …) before the Firestore write
// round-trips). Then it returns [items, setItems] like useState; the listener
// still overwrites with server truth on the next snapshot. Read-only callers get
// just the array.
//
// Pass { sort } with a comparator to keep the items in a stable order (the old
// inline listeners sorted in the snapshot callback, e.g. locale-aware by name).
// The comparator is held in a ref so passing it inline neither re-subscribes the
// listener nor trips react-hooks/exhaustive-deps.
export function useProjectCollection(projectId, name, { withSetter = false, sort } = {}) {
  const [items, setItems] = useState([]);
  const sortRef = useRef(sort);
  sortRef.current = sort;

  useEffect(() => {
    if (!projectId) {
      setItems([]);
      return undefined;
    }
    const unsubscribe = onSnapshot(
      collection(db, "projects", projectId, name),
      (snapshot) => {
        const docs = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        const comparator = sortRef.current;
        setItems(comparator ? [...docs].sort(comparator) : docs);
      },
      (err) => console.error(`${name} listener:`, err)
    );
    return () => unsubscribe();
  }, [projectId, name]);

  return withSetter ? [items, setItems] : items;
}
